import { createContext, useContext, useEffect, useState } from "react";
import { Project } from "../../models/Project";
import { listProjects } from "./service";
import { retry } from "../../utils/async";
import { useCurrentUser, useOrganization } from "../user/UserDispatcher";
import { Feature } from "../../models/Feature";
import { I18nContext, ReplacedTranslation } from "../../I18nLoader";
import { ProjectLocalization, ProjectLocalizationOriginalString } from "../../models/ProjectLocalization";
import { ThemeContext } from "../ThemeContextProvider/ThemeContextProvider";
import { getInitialI18n, getLanguage, text } from "../../utils/i18n";

/**
 * Fetches and provides a list of projects.
 */
export const ProjectContextProvider: React.FC = ({ children }) => {
  const i18nContext = useContext(I18nContext);
  const themeContext = useContext(ThemeContext);
  const organization = useOrganization();
  const currentUser = useCurrentUser();
  const storageKey = 'currentProjectId_' + organization.id;

  const [projects, setProjects] = useState<Project[]>([]);
  const [currentProjectId, setCurrentProjectId] = useState<string | null>(() => {
    let value = localStorage.getItem(storageKey);
    return value === "null" ? null : value;
  });
  const [isRefreshing, setRefreshing] = useState<boolean>(false);
  const [hasRefreshedOnce, setHasRefreshedOnce] = useState(false);

  async function refreshProjects(): Promise<Project[]> {
    setRefreshing(true);

    try {
      const projects = await retry(() => listProjects());
      setProjects(projects);

      return projects;
    } catch (error) {
      console.error("Error refreshing projects", error);
    } finally {
      setRefreshing(false);
      setHasRefreshedOnce(true);
    }

    return projects;
  }

  const isFeatureEnabled = organization.hasFeature(Feature.Project);

  useEffect(() => {
    if (isFeatureEnabled) {
      refreshProjects();
    } else {
      setProjects([]);
    }
  }, [isFeatureEnabled]);

  const projectIds = projects.map(project => project.id);
  useEffect(() => {
    if (!hasRefreshedOnce) {
      return;
    }

    const defaultProject = projects.find(project => project.isDefault);

    //  Admins can select the "no project" option, and that option should be remembered til next reload.
    setCurrentProjectId(projectId => {
      if (!localStorage.getItem(storageKey) && defaultProject) {
        projectId = defaultProject.id;

      } else if (!currentUser.isAdmin && !projectIds.includes(projectId)) {
        projectId = defaultProject?.id ?? projects[0]?.id ?? null;

      } else if (currentUser.isAdmin && projectId && !projectIds.includes(projectId)) {
        projectId = defaultProject?.id ?? null;
      }

      //  Måste spara någonting i localstorage för att inte gå på projekt som är isDefault varje gång.
      localStorage.setItem(storageKey, projectId ?? 'null');

      return projectId;
    });
  }, [hasRefreshedOnce, projectIds.join(), currentProjectId]);

  const currentProject = currentProjectId ?
    projects.find(project => project.id === currentProjectId) || null : null;

  useEffect(() => {
    themeContext?.setCurrentThemeId(currentProject?.themeId);
  }, [currentProject?.themeId]);

  useEffect(() => {
    if (!i18nContext) {
      return;
    }

    const originalStrings = ProjectLocalization.getOriginalStrings();

    if (currentProject?.localization) {
      //  Add the localization strings as i18n translations.
      //  This way we can utilize the "text" function and avoid having to write a lot of code
      //  to use project localization strings.
      const localizationPairs: [ProjectLocalizationOriginalString, string][] = [];
      originalStrings.order.forEach((original, index) => {
        if (currentProject.localization.order[index]) {
          localizationPairs.push([original, currentProject.localization.order[index]]);
        }
      });

      //  "Lägg order" behandlas samma som "Lägg till order"
      if (getLanguage() === 'se') {
        localizationPairs.push([
          { requiresContext: false, string: 'lägg order' },
          currentProject.localization.order[6] && currentProject.localization.order[0] ?
            `${currentProject.localization.order[6]} ${currentProject.localization.order[0]}` :
            'lägg order'
        ]);
      }

      i18nContext.replaceTranslations(localizationPairs.reduce((translations, pair) => {
        let originalLanguagePhrase = pair[0].string;
        if (pair[0].requiresContext) {
          //  Using [order] fixes all occurrences of "Lägg till" becoming "Boka", for example.
          originalLanguagePhrase = '[order] ' + originalLanguagePhrase;
        }

        translations.push({
          originalLanguagePhrase,
          newPhrase: pair[1],
          language: getLanguage()
        });

        return translations;
      }, [] as ReplacedTranslation[]));
    } else {
      //  Reset translations.
      const replacedTranslations = [...originalStrings.order];
      if (getLanguage() === 'se') {
        replacedTranslations.push({
          requiresContext: false,
          string: 'lägg order'
        });
      }

      i18nContext.replaceTranslations(replacedTranslations.map(originalTranslation => {
        return {
          originalLanguagePhrase: originalTranslation.requiresContext ?
            '[order] ' + originalTranslation.string : originalTranslation.string,
          newPhrase: getInitialI18n().get(originalTranslation.string), //  getI18n() är ju modifierad.
          language: getLanguage()
        };
      }, [] as ReplacedTranslation[]));
    }
  }, [currentProject?.localization]);

  if (isFeatureEnabled && !hasRefreshedOnce) {
    //  Projects state can greatly impact UI.
    return null;
  }

  return (
    <ProjectContext.Provider value={{
      isRefreshing,
      projects,
      refreshProjects,
      setCurrentProjectId: id => setCurrentProjectId(id),
      currentProject,
      reorderProjects: orderedIds => {
        setProjects(projects =>
          [...projects].sort((a, b) => orderedIds.indexOf(a.id) - orderedIds.indexOf(b.id)));
      },
    }}>
      {children}
    </ProjectContext.Provider>
  )
}

export const ProjectContext = createContext<{
  projects: Project[];
  currentProject: Project | null;
  isRefreshing: boolean;
  setCurrentProjectId: (projectId: string) => void;
  refreshProjects: () => Promise<Project[]>;
  reorderProjects: (orderedIds: string[]) => void;
}>(null);